/**
 * OfflineBanner — displays a persistent banner when the user is offline,
 * and a brief "reconnected" toast when connectivity returns.
 *
 * Also shows pending mutation count if there are queued offline writes.
 */

import { useEffect, useRef } from "react";
import { WifiOff, Wifi, CloudOff, RotateCw } from "lucide-react";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { useMutationReplay } from "../../hooks/useMutationReplay";
import { toast } from "sonner";

export function OfflineBanner() {
  const { isOnline, isServerReachable, wasOffline } = useOnlineStatus();
  const { pendingCount, replayNow } = useMutationReplay();
  const hasShownReconnect = useRef(false);

  // Show "reconnected" toast when coming back online
  useEffect(() => {
    if (wasOffline && isOnline && !hasShownReconnect.current) {
      hasShownReconnect.current = true;
      toast.success("\u05D4\u05D7\u05D9\u05D1\u05D5\u05E8 \u05D7\u05D6\u05E8! \u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05DE\u05E1\u05E0\u05DB\u05E8\u05E0\u05EA...", {
        icon: <Wifi className="w-4 h-4" />,
        duration: 3000,
      });
    }
    if (!wasOffline) {
      hasShownReconnect.current = false;
    }
  }, [wasOffline, isOnline]);

  // Don't render anything if we're online and everything is fine
  const showBanner = !isOnline || isServerReachable === false;
  const showPendingOnly = isOnline && isServerReachable !== false && pendingCount > 0;

  if (!showBanner && !showPendingOnly) return null;

  // Pending mutations indicator (shown when online but queue has items)
  if (showPendingOnly) {
    return (
      <div
        className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 rounded-lg px-3 py-2 text-xs shadow-lg"
        style={{
          backgroundColor: "#f59e0b",
          color: "#fff",
          direction: "rtl",
          fontFamily: "var(--font-family)",
        }}
      >
        <RotateCw className="w-3.5 h-3.5 animate-spin" />
        <span>{pendingCount} {"\u05E4\u05E2\u05D5\u05DC\u05D5\u05EA \u05DE\u05DE\u05EA\u05D9\u05E0\u05D5\u05EA \u05DC\u05E1\u05E0\u05DB\u05E8\u05D5\u05DF"}</span>
        <button
          type="button"
          className="btnReset underline cursor-pointer mr-1"
          onClick={replayNow}
        >
          {"\u05E1\u05E0\u05DB\u05E8\u05DF \u05E2\u05DB\u05E9\u05D9\u05D5"}
        </button>
      </div>
    );
  }

  // Full offline banner
  const isServerDown = isOnline && isServerReachable === false;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-3 px-4 py-2.5 shadow-md"
      style={{
        backgroundColor: isServerDown ? "#dc2626" : "#6b7280",
        color: "#fff",
        direction: "rtl",
        fontFamily: "var(--font-family)",
      }}
    >
      {isServerDown ? (
        <>
          <CloudOff className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">
            {"\u05D4\u05E9\u05E8\u05EA \u05D0\u05D9\u05E0\u05D5 \u05D6\u05DE\u05D9\u05DF. \u05E9\u05D9\u05E0\u05D5\u05D9\u05D9\u05DD \u05E9\u05E0\u05E2\u05E9\u05D5 \u05D9\u05D9\u05E9\u05DE\u05E8\u05D5 \u05D1\u05DE\u05DB\u05E9\u05D9\u05E8 \u05D5\u05D9\u05E1\u05D5\u05E0\u05DB\u05E8\u05E0\u05D5 \u05DB\u05E9\u05D4\u05E9\u05E8\u05EA \u05D9\u05D7\u05D6\u05D5\u05E8."}
          </span>
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">
            {"\u05D0\u05D9\u05DF \u05D7\u05D9\u05D1\u05D5\u05E8 \u05DC\u05D0\u05D9\u05E0\u05D8\u05E8\u05E0\u05D8. \u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05E4\u05D5\u05E2\u05DC\u05EA \u05D1\u05DE\u05E6\u05D1 \u05D0\u05D5\u05E4\u05DC\u05D9\u05D9\u05DF \u05E2\u05DD \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05E9\u05E0\u05E9\u05DE\u05E8\u05D5."}
          </span>
        </>
      )}

      {pendingCount > 0 && (
        <span
          className="rounded-full px-2 py-0.5 text-xs"
          style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
        >
          {pendingCount} {"\u05E4\u05E2\u05D5\u05DC\u05D5\u05EA \u05D1\u05EA\u05D5\u05E8"}
        </span>
      )}
    </div>
  );
}
